import { ErdDiagram } from '@/components/docs/erd-diagram';
import { docsDataApi } from '@/lib/api';
import { API_BASE_URL } from '@/lib/api-base';
import { buildErdMermaid } from '@mss_creator/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/admin/docs-data')({
  component: DocsDataPage,
});

/**
 * pms_admin-role gating is enforced server-side (403 on every route); this page
 * assumes it's only ever linked to from the pms_admin-gated rail item in
 * AppRail — same convention as routes/admin.claude-credentials.tsx.
 *
 * Exported (rather than kept file-local) so tests/admin-docs-data.test.tsx can
 * render it directly under a QueryClientProvider without needing a full
 * router harness.
 */
export function DocsDataPage() {
  const docsDataQuery = useQuery({
    queryKey: ['docs-data'],
    queryFn: () => docsDataApi.get(),
  });

  if (docsDataQuery.isError && !docsDataQuery.data) {
    return (
      <p className="text-danger text-small">
        Impossible de charger la documentation.
        {docsDataQuery.error instanceof Error ? ` ${docsDataQuery.error.message}` : ''}
      </p>
    );
  }

  if (!docsDataQuery.data) {
    return <p className="text-gray-500 text-small">Chargement…</p>;
  }

  const { tables, endpoints } = docsDataQuery.data;

  return (
    <section className="mx-auto max-w-5xl space-y-8 px-6 py-8">
      <div className="flex items-center justify-between">
        <h1 className="font-bold text-bleu-nuit text-h2">Documentation technique</h1>
        <a
          href={`${API_BASE_URL}/docs`}
          target="_blank"
          rel="noreferrer"
          className="rounded-md border border-bleu-nuit/30 px-3 py-1 text-bleu-nuit text-small hover:bg-bleu-nuit/5"
        >
          Référence API interactive (Scalar) ↗
        </a>
      </div>

      <section className="space-y-2">
        <h2 className="font-semibold text-gray-900 text-h4">Schéma relationnel</h2>
        <ErdDiagram diagram={buildErdMermaid(docsDataQuery.data)} />
      </section>

      <section className="space-y-4">
        <h2 className="font-semibold text-gray-900 text-h4">Tables</h2>
        {tables.map((table) => (
          <div key={table.name} className="space-y-2 rounded-md border border-gray-200 p-4">
            <h3 className="font-mono font-semibold text-bleu-nuit">
              {table.name} <span className="font-normal text-gray-400">({table.tsName})</span>
            </h3>
            {table.businessRules && (
              <p className="whitespace-pre-line text-gray-700 text-small">{table.businessRules}</p>
            )}
            <table className="w-full text-left text-small">
              <thead>
                <tr className="text-gray-500">
                  <th className="pr-4 font-semibold">Colonne</th>
                  <th className="pr-4 font-semibold">Type</th>
                  <th className="pr-4 font-semibold">Contraintes</th>
                </tr>
              </thead>
              <tbody>
                {table.columns.map((column) => (
                  <tr key={column.name} className="border-gray-100 border-t">
                    <td className="pr-4 font-mono">{column.name}</td>
                    <td className="pr-4">{column.dataType}</td>
                    <td className="pr-4 text-gray-500">
                      {[
                        column.primaryKey && 'PK',
                        column.notNull && 'NOT NULL',
                        column.default && `default: ${column.default}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {table.foreignKeys.length > 0 && (
              <p className="text-caption text-gray-500">
                Références :{' '}
                {table.foreignKeys
                  .map((fk) => `${fk.columns.join(', ')} → ${fk.referencedTable}`)
                  .join(' · ')}
              </p>
            )}
          </div>
        ))}
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold text-gray-900 text-h4">Endpoints</h2>
        <ul className="divide-y divide-gray-100 text-small">
          {endpoints.map((endpoint) => (
            <li key={`${endpoint.method} ${endpoint.path}`} className="flex flex-col gap-1 py-2">
              <span>
                <span className="font-mono font-semibold text-bleu-nuit">{endpoint.method}</span>{' '}
                <span className="font-mono">{endpoint.path}</span>
              </span>
              {endpoint.summary && <span className="text-gray-500">{endpoint.summary}</span>}
              {endpoint.touchesTables.length > 0 && (
                <span className="text-caption text-gray-400">
                  Tables : {endpoint.touchesTables.join(', ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
